import { getCurrentUser } from '@/lib/auth-utils'
import { fetchAccessibleCompanyIds } from '@/lib/company-access'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { CompanyMedia, Screen, ScreenEvent, ScreenPlaylistItem } from '@/models'
import { neon } from '@/services/neon'
import { deleteStorageObject, STORAGE_FOLDERS, uploadStorageFile } from '@/services/storage'

function getUserId(): string {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }
  return user.id
}

async function checkScreenAccess(
  screenId: string,
  action: 'view' | 'add' | 'edit' | 'delete',
): Promise<Screen> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  const { data, error } = await neon
    .from('screens')
    .select('*')
    .eq('id', screenId)
    .is('deleted_at', null)
    .single()
  if (error) {
    throw new Error(error.message)
  }

  const screen = data as Screen
  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!screen.company_id || !companyIds.includes(screen.company_id)) {
      throw new Error(`You do not have permission to ${action} this screen.`)
    }
  }

  return screen
}

// ─── Screens ────────────────────────────────────────────────────

export async function fetchScreens(companyId?: string): Promise<Screen[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  let query = neon
    .from('screens')
    .select('*')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (user.role === ROLE_ADMIN) {
    if (companyId) {
      query = query.eq('company_id', companyId)
    }
  } else {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (companyIds.length === 0) {
      return []
    }
    if (companyId) {
      if (!companyIds.includes(companyId)) {
        return []
      }
      query = query.eq('company_id', companyId)
    } else {
      query = query.in('company_id', companyIds)
    }
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)
  return (data as Screen[]) || []
}

export async function fetchScreenById(id: string): Promise<Screen> {
  return checkScreenAccess(id, 'view')
}

export async function createScreen(
  payload: Partial<Screen> & { company_id: string; name: string },
): Promise<Screen> {
  const userId = getUserId()

  if (getCurrentUser()?.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(userId)
    if (!companyIds.includes(payload.company_id)) {
      throw new Error('You do not have permission to create screens for this company.')
    }
  }

  const { data, error } = await neon
    .from('screens')
    .insert({ ...payload, created_by: userId })
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data as Screen
}

export async function updateScreen(id: string, payload: Partial<Screen>): Promise<Screen> {
  const existing = await checkScreenAccess(id, 'edit')

  const { data, error } = await neon
    .from('screens')
    .update(payload)
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) throw new Error('Screen not found or permission revoked.')
    throw new Error(error.message)
  }
  return data as Screen
}

export async function deleteScreen(id: string): Promise<void> {
  const existing = await checkScreenAccess(id, 'delete')

  const { error, data } = await (neon.from('screens') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return
    throw new Error(error.message)
  }
  if (!data) return
}

// ─── Company Media ──────────────────────────────────────────────

export async function fetchCompanyMedia(
  companyId: string,
  type?: 'ad' | 'info',
): Promise<CompanyMedia[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!companyIds.includes(companyId)) {
      throw new Error('You do not have permission to view media for this company.')
    }
  }

  let query = neon
    .from('company_media')
    .select('*')
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (type) {
    query = query.eq('type', type)
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)
  return (data as CompanyMedia[]) || []
}

export async function createCompanyMedia(
  companyId: string,
  type: 'ad' | 'info',
  title: string,
  file: File,
): Promise<CompanyMedia> {
  const userId = getUserId()

  if (getCurrentUser()?.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(userId)
    if (!companyIds.includes(companyId)) {
      throw new Error('You do not have permission to add media to this company.')
    }
  }

  const prefix = type === 'ad' ? 'ad' : 'info'
  const filename = `${prefix}-${Date.now()}-${file.name}`

  const { key } = await uploadStorageFile(STORAGE_FOLDERS.SCREEN_PHOTOS, filename, file, file.type)

  const { data, error } = await neon
    .from('company_media')
    .insert({
      company_id: companyId,
      type,
      title,
      file_key: key,
      file_type: file.type,
      file_size: file.size,
      created_by: userId,
    })
    .select()
    .single()

  if (error) {
    await deleteStorageObject(key).catch(() => {})
    throw new Error(error.message)
  }

  return data as CompanyMedia
}

export async function deleteCompanyMedia(id: string, fileKey: string): Promise<void> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('User not authenticated')

  const { data: media } = await neon
    .from('company_media')
    .select('company_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()
  const companyId = (media as { company_id?: string } | null)?.company_id
  if (!companyId) throw new Error('Media not found.')

  if (user.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(user.id)
    if (!companyIds.includes(companyId)) {
      throw new Error('You do not have permission to delete this media.')
    }
  }

  const { error, data } = await (neon.from('company_media') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return
    throw new Error(error.message)
  }
  if (!data) return

  await deleteStorageObject(fileKey).catch(() => {})
}

// ─── Screen Events ──────────────────────────────────────────────

export async function recordHeartbeat(screenId: string): Promise<void> {
  const now = new Date().toISOString()
  const { error: evtErr } = await neon.from('screen_events').insert({
    screen_id: screenId,
    event_type: 'heartbeat',
    payload: { timestamp: now },
  })
  if (evtErr) throw new Error(evtErr.message)

  const { error: updErr } = await neon
    .from('screens')
    .update({ last_online_at: now })
    .eq('id', screenId)
  if (updErr) throw new Error(updErr.message)
}

export async function recordSlideView(
  screenId: string,
  slideIndex: number,
  slideType: string,
  slideRefId?: string,
): Promise<void> {
  const { error } = await neon.from('screen_events').insert({
    screen_id: screenId,
    event_type: 'slide_view',
    payload: { slide_index: slideIndex, slide_type: slideType, slide_ref_id: slideRefId },
  })
  if (error) throw new Error(error.message)
}

export async function fetchScreenEvents(
  screenId: string,
  eventType?: 'heartbeat' | 'slide_view',
  limit = 100,
): Promise<ScreenEvent[]> {
  const user = getCurrentUser()
  if (!user?.id) {
    throw new Error('User not authenticated')
  }

  await checkScreenAccess(screenId, 'view')

  let query = neon
    .from('screen_events')
    .select('*')
    .eq('screen_id', screenId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (eventType) {
    query = query.eq('event_type', eventType)
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)
  return (data as ScreenEvent[]) || []
}

export async function fetchScreenStats(screenId: string): Promise<{
  lastOnlineAt: string | null
  totalHoursDisplayed: number
  recentHeartbeats: ScreenEvent[]
  totalSlideViews: number
}> {
  const screen = await checkScreenAccess(screenId, 'view')

  const [heartbeatsRes, slideViewsRes] = await Promise.all([
    neon
      .from('screen_events')
      .select('*')
      .eq('screen_id', screenId)
      .eq('event_type', 'heartbeat')
      .order('created_at', { ascending: false })
      .limit(50),
    neon
      .from('screen_events')
      .select('id', { count: 'exact', head: true })
      .eq('screen_id', screenId)
      .eq('event_type', 'slide_view'),
  ] as any)

  const heartbeats = ((heartbeatsRes as any).data as ScreenEvent[]) || []
  const slideViews = (slideViewsRes as any).count ?? 0

  return {
    lastOnlineAt: screen.last_online_at ?? null,
    totalHoursDisplayed: screen.total_hours_displayed,
    recentHeartbeats: heartbeats,
    totalSlideViews: slideViews,
  }
}

// ─── Playlist Builder ───────────────────────────────────────────

export async function buildSlideDeck(
  screen: Screen,
  media: CompanyMedia[],
): Promise<
  Array<{
    type: 'media' | 'announcement'
    refId: string
    title: string
    content?: string
    fileKey?: string
    duration: number
    bgColor?: string
  }>
> {
  getUserId()

  const items: Array<{
    type: 'media' | 'announcement'
    refId: string
    title: string
    content?: string
    fileKey?: string
    duration: number
    bgColor?: string
  }> = []

  if (screen.playlist_order && screen.playlist_order.length > 0) {
    for (const entry of screen.playlist_order) {
      if (entry.type === 'media') {
        const m = media.find((mm) => mm.id === entry.ref_id)
        if (m) {
          items.push({
            type: 'media',
            refId: m.id,
            title: m.title,
            fileKey: m.file_key,
            duration: entry.duration ?? screen.slide_duration,
          })
        }
      } else if (entry.type === 'announcement') {
        const ann = await fetchAnnouncementForPlaylist(entry.ref_id)
        if (ann) {
          items.push({
            type: 'announcement',
            refId: ann.id,
            title: ann.title,
            content: ann.content,
            duration: entry.duration ?? screen.slide_duration,
            bgColor: screen.announcement_bg_colors[ann.id] ?? ann.background_color,
          })
        }
      }
    }
  } else {
    for (const m of media) {
      items.push({
        type: 'media',
        refId: m.id,
        title: m.title,
        fileKey: m.file_key,
        duration: screen.slide_duration,
      })
    }
    if (screen.show_announcements && screen.selected_announcement_ids.length > 0) {
      for (const annId of screen.selected_announcement_ids) {
        const ann = await fetchAnnouncementForPlaylist(annId)
        if (ann) {
          items.push({
            type: 'announcement',
            refId: ann.id,
            title: ann.title,
            content: ann.content,
            duration: screen.slide_duration,
            bgColor: screen.announcement_bg_colors[ann.id] ?? ann.background_color,
          })
        }
      }
    }
  }

  return items
}

interface AnnouncementBrief {
  id: string
  title: string
  content: string
  background_color?: string
}

async function fetchAnnouncementForPlaylist(id: string): Promise<AnnouncementBrief | null> {
  try {
    const { data, error } = await neon
      .from('announcements')
      .select('id, title, content, background_color')
      .eq('id', id)
      .single()
    if (error) return null
    return data as AnnouncementBrief
  } catch {
    return null
  }
}
